import { createContext, useContext, useState } from 'react';
import { Check, Copy, Download, Key, LogOut, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { apiError, crmAPI, useCRMAuth } from '../context/CRMAuthContext';
import '../crm.css';
import './mfa.css';

const RecoveryContext = createContext(() => {});
export const useShowRecoveryCodes = () => useContext(RecoveryContext);

// Password -> QR/secret -> code. Calls onEnabled(recoveryCodes) on success.
export function MFAEnrollmentForm({ onEnabled }) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [setup, setSetup] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async event => {
    event.preventDefault(); setError(''); setBusy(true);
    try {
      if (setup) {
        const { data } = await crmAPI.post('/auth/2fa/enable', { code });
        onEnabled(data.recovery_codes || []);
      } else {
        setSetup((await crmAPI.post('/auth/2fa/setup', { password })).data);
        setPassword('');
      }
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };
  return <form onSubmit={submit} className="crm-mfa-enroll" data-testid="crm-mfa-form">
    {!setup ? <>
      <ol className="crm-mfa-steps">
        <li>Telefonunuza <strong>Google Authenticator</strong>, <strong>Microsoft Authenticator</strong> veya <strong>Authy</strong> yükleyin.</li>
        <li>Kimliğinizi doğrulamak için mevcut şifrenizi girin.</li>
      </ol>
      <label htmlFor="crm-mfa-password">Mevcut Şifre</label>
      <Input id="crm-mfa-password" data-testid="crm-mfa-password" required type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} />
    </> : <>
      <p className="crm-mfa-hint">Uygulamada <strong>QR kodu tara</strong>yı seçin ve aşağıdaki kodu okutun.</p>
      {setup.qr && <img className="crm-mfa-qr" src={setup.qr} alt="2FA QR kodu" data-testid="crm-mfa-qr" />}
      <details className="crm-mfa-manual"><summary><Key size={13} /> QR okutamıyor musunuz? Anahtarı elle girin</summary>
        <code className="crm-mfa-secret" data-testid="crm-mfa-secret">{setup.secret}</code>
      </details>
      <label htmlFor="crm-mfa-code">Uygulamadaki 6 haneli kod</label>
      <Input id="crm-mfa-code" data-testid="crm-mfa-code" required inputMode="numeric" pattern="[0-9]{6}" autoComplete="one-time-code" maxLength={6} autoFocus value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} />
    </>}
    {error && <p className="crm-error" role="alert" data-testid="crm-mfa-error">{error}</p>}
    <Button type="submit" className="crm-primary crm-login-submit" disabled={busy} data-testid="crm-mfa-submit">
      {busy ? 'İşleniyor…' : setup ? 'Doğrula ve Etkinleştir' : 'Kurulumu Başlat'}
    </Button>
  </form>;
}

function RecoveryCodes({ codes, onDone }) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const text = codes.join('\n');
  const copy = async () => { try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_) { /* clipboard blocked */ } };
  const download = () => {
    const url = URL.createObjectURL(new Blob([`CRM kurtarma kodlari (her biri tek kullanimlik)\n\n${text}\n`], { type: 'text/plain' }));
    const link = Object.assign(document.createElement('a'), { href: url, download: 'crm-recovery-codes.txt' });
    link.click(); URL.revokeObjectURL(url); setSaved(true);
  };
  return <div className="crm-mfa-enroll" data-testid="crm-mfa-recovery">
    <p className="crm-mfa-hint">İki aşamalı doğrulama etkin. Telefonunuzu kaybederseniz giriş yapmak için bu <strong>tek kullanımlık kurtarma kodlarını</strong> kullanabilirsiniz. <strong>Bir daha gösterilmeyecekler.</strong></p>
    <ul className="crm-mfa-codes" data-testid="crm-mfa-recovery-codes">{codes.map(code => <li key={code}><code>{code}</code></li>)}</ul>
    <div className="crm-mfa-actions">
      <Button type="button" variant="outline" onClick={copy} data-testid="crm-mfa-copy">{copied ? <Check size={15} /> : <Copy size={15} />}{copied ? 'Kopyalandı' : 'Kopyala'}</Button>
      <Button type="button" variant="outline" onClick={download} data-testid="crm-mfa-download"><Download size={15} />İndir</Button>
    </div>
    <label className="crm-mfa-confirm"><input type="checkbox" checked={saved} onChange={e => setSaved(e.target.checked)} data-testid="crm-mfa-saved" /> Kodları güvenli bir yere kaydettim</label>
    <Button type="button" className="crm-primary crm-login-submit" disabled={!saved} onClick={onDone} data-testid="crm-mfa-done">Yeniden giriş yap</Button>
  </div>;
}

const Screen = ({ title, eyebrow, children, footer }) => <div className="crm-root" lang="tr">
  <div className="crm-login crm-mfa-screen" data-testid="crm-mfa-screen">
    <div className="crm-login-photo" />
    <div className="crm-login-top"><span className="crm-wordmark">BROKER<span>CRM</span></span><span className="crm-login-label">KURUMSAL ERİŞİM</span></div>
    <main className="crm-login-panel">
      <div className="crm-login-symbol"><ShieldCheck size={24} /></div>
      <p className="crm-eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      <div className="crm-heading-line" />
      {children}
      {footer}
    </main>
  </div>
</div>;

// Wraps all staff routes: blocks the app until 2FA is enrolled and keeps the
// one-time recovery codes on screen even after the session is rotated.
export function StaffMFAGate({ children }) {
  const { user, mfa_enrollment_required: required, logout, reset } = useCRMAuth();
  const [codes, setCodes] = useState(null);
  const finish = () => { setCodes(null); reset(); };
  if (codes) return <Screen eyebrow="KURTARMA KODLARI" title="Kodlarınızı saklayın"><RecoveryCodes codes={codes} onDone={finish} /></Screen>;
  if (user && required) return <Screen eyebrow="GÜVENLİK ZORUNLULUĞU" title="İki aşamalı doğrulamayı kurun"
    footer={<button type="button" className="crm-mfa-logout" onClick={() => logout().catch(() => reset())} data-testid="crm-mfa-logout"><LogOut size={14} />Çıkış yap ({user.username || user.email})</button>}>
    <p className="crm-mfa-hint">Devam etmeden önce hesabınız için iki aşamalı doğrulama gereklidir.</p>
    <MFAEnrollmentForm onEnabled={setCodes} />
  </Screen>;
  return <RecoveryContext.Provider value={setCodes}>{children}</RecoveryContext.Provider>;
}
